import React, { Suspense } from 'react'
import { Outlet } from 'react-router'
import { Spinner } from '@agion/ui'
import { Sidebar } from './AnalyticsSidebar'
import { cn } from '@/core/analytics_utils'

const COMPACT_VIEWPORT_QUERY = '(max-width: 767px)'

function compactViewportMatches(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(COMPACT_VIEWPORT_QUERY).matches
}

export const MainLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false)
  const [compactViewport, setCompactViewport] = React.useState(compactViewportMatches)

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const media = window.matchMedia(COMPACT_VIEWPORT_QUERY)
    const update = () => setCompactViewport(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  const collapsed = sidebarCollapsed || compactViewport

  return (
    <div className="flex h-screen bg-[var(--background)]">
      <aside
        className={cn(
          'bg-slate-50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700/50 flex-shrink-0 transition-all duration-300',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        <Sidebar
          collapsed={collapsed}
          collapseLocked={compactViewport}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </aside>

      <main className="min-w-0 flex-1 overflow-auto bg-[var(--background)]">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <Spinner size="lg" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}
